/**
 * Offline Storage Service
 * Handles localStorage caching for listings, messages, profiles, wishlist,
 * and queued actions (drafts, offers) when offline.
 */

const KEYS = {
  LISTINGS: 'barter_offline_listings',
  MESSAGES: 'barter_offline_messages',
  PROFILES: 'barter_offline_profiles',
  WISHLIST: 'barter_offline_wishlist',
  ACTION_QUEUE: 'barter_offline_queue',
  DRAFTS: 'barter_offline_drafts',
};

const TTL = {
  LISTINGS: 30 * 60 * 1000,   // 30 min
  MESSAGES: 10 * 60 * 1000,   // 10 min
  PROFILES: 60 * 60 * 1000,   // 1 hour
  WISHLIST: 30 * 60 * 1000,   // 30 min
};

// ─── helpers ────────────────────────────────────────────────────────────────

function lsGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    // Storage quota exceeded – silently fail
    console.warn('[OfflineStorage] localStorage write failed:', e?.message);
    return false;
  }
}

function isExpired(entry) {
  return !entry || !entry?.expiry || Date.now() > entry?.expiry;
}

// ─── listings ───────────────────────────────────────────────────────────────

export function cacheListings(listings) {
  lsSet(KEYS?.LISTINGS, { data: listings, expiry: Date.now() + TTL?.LISTINGS });
}

export function getCachedListings() {
  const entry = lsGet(KEYS?.LISTINGS);
  if (isExpired(entry)) return null;
  return entry?.data ?? null;
}

// ─── messages / conversations ────────────────────────────────────────────────

export function cacheConversations(userId, conversations) {
  const all = lsGet(KEYS?.MESSAGES) || {};
  all[userId] = { data: conversations, expiry: Date.now() + TTL?.MESSAGES };
  lsSet(KEYS?.MESSAGES, all);
}

export function getCachedConversations(userId) {
  const all = lsGet(KEYS?.MESSAGES) || {};
  const entry = all?.[userId];
  if (isExpired(entry)) return null;
  return entry?.data ?? null;
}

// ─── profiles ────────────────────────────────────────────────────────────────

export function cacheProfile(userId, profile) {
  const all = lsGet(KEYS?.PROFILES) || {};
  all[userId] = { data: profile, expiry: Date.now() + TTL?.PROFILES };
  lsSet(KEYS?.PROFILES, all);
}

export function getCachedProfile(userId) {
  const all = lsGet(KEYS?.PROFILES) || {};
  const entry = all?.[userId];
  if (isExpired(entry)) return null;
  return entry?.data ?? null;
}

// ─── wishlist ────────────────────────────────────────────────────────────────

export function cacheWishlist(userId, items) {
  const all = lsGet(KEYS?.WISHLIST) || {};
  all[userId] = { data: items, expiry: Date.now() + TTL?.WISHLIST };
  lsSet(KEYS?.WISHLIST, all);
}

export function getCachedWishlist(userId) {
  const all = lsGet(KEYS?.WISHLIST) || {};
  const entry = all?.[userId];
  if (isExpired(entry)) return null;
  return entry?.data ?? null;
}

// ─── action queue (offers, messages sent while offline) ──────────────────────

export function enqueueAction(action) {
  // action: { id, type, payload, createdAt }
  const queue = lsGet(KEYS?.ACTION_QUEUE) || [];
  const item = {
    id: action?.id || `q_${Date.now()}_${Math.random()?.toString(36)?.slice(2)}`,
    type: action?.type,
    payload: action?.payload,
    createdAt: action?.createdAt || new Date()?.toISOString(),
  };
  queue?.push(item);
  lsSet(KEYS?.ACTION_QUEUE, queue);
  return item;
}

export function getActionQueue() {
  return lsGet(KEYS?.ACTION_QUEUE) || [];
}

export function removeFromQueue(id) {
  const queue = lsGet(KEYS?.ACTION_QUEUE) || [];
  lsSet(KEYS?.ACTION_QUEUE, queue?.filter(a => a?.id !== id));
}

export function clearActionQueue() {
  lsSet(KEYS?.ACTION_QUEUE, []);
}

// ─── drafts ──────────────────────────────────────────────────────────────────

export function saveDraft(key, data) {
  const drafts = lsGet(KEYS?.DRAFTS) || {};
  drafts[key] = { data, savedAt: new Date()?.toISOString() };
  lsSet(KEYS?.DRAFTS, drafts);
}

export function getDraft(key) {
  const drafts = lsGet(KEYS?.DRAFTS) || {};
  return drafts?.[key]?.data ?? null;
}

export function deleteDraft(key) {
  const drafts = lsGet(KEYS?.DRAFTS) || {};
  delete drafts?.[key];
  lsSet(KEYS?.DRAFTS, drafts);
}

export function getAllDrafts() {
  return lsGet(KEYS?.DRAFTS) || {};
}
